/**
 * <s-patch-customizer> — Drag-and-drop sleeve patch designer for the varsity jacket.
 * Markup and config: blocks/patch-customizer.liquid.
 *
 * - CUSTOMIZE YOUR JACKET swaps the product photo gallery for the design canvas (same spot, same width);
 *   BACK TO PHOTOS swaps back. The design lives in memory, so switching never loses it.
 * - Patches are added by tapping a design in the tray or dragging it onto the jacket, then moved freely
 *   with mouse, touch or arrow keys, and resized with corner handles, the slider, − / + or the +/- keys.
 *   A patch must sit fully inside a sleeve zone of its view and may not overlap another patch.
 * - Each patch stores its view, sleeve, centre (% of the photo) and width in inches. Inches come from the
 *   planning scale in the block settings (photo width in inches); height keeps the artwork's proportions.
 * - Until "Patch sizes are verified" is on, resized patches can be tested but not ordered: Add to cart is
 *   blocked until every patch is back at the standard size, and orders never carry unverified inches.
 * - The design is written into hidden properties[...] inputs tied to the product form (form attribute),
 *   and the matching Patch Customization variant is exposed to <s-product-form> via data-extra-lines.
 * - SAVE MY DESIGN stores the design on the device; ?design=<base64> restores a design (order link).
 */
class SPatchCustomizer extends HTMLElement {
  #ac;
  #cfg;
  #patches = [];
  #view = null;
  #selected = null;
  #drag = null;
  #dragMove = () => {};
  #designId = '';
  #canvas = null;
  #gallery = null;
  #isOpen = false;

  connectedCallback() {
    this.#ac = new AbortController();
    try {
      this.#cfg = JSON.parse(this.querySelector('[data-config]')?.textContent || '{}');
    } catch (e) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const inEditor = this.#cfg.editor || !!window.Shopify?.designMode;
    if (!this.#cfg.live && !inEditor && params.get('customizer') !== 'preview') return;
    if (!this.#cfg.views?.length) return;

    const sz = this.#cfg.size || {};
    this.#cfg.size = {
      min: Number(sz.min) || 2,
      max: Math.max(Number(sz.max) || 4, Number(sz.min) || 2),
      default: Number(sz.default) || 3,
      photoWidth: Number(sz.photoWidth) || 34,
      verified: !!sz.verified,
    };

    this.hidden = false;
    this.style.display = '';
    this.#canvas = this.querySelector('[data-canvas]');
    this.#gallery = document.querySelector('s-product-gallery');
    this.#view = this.#cfg.views[0].id;
    this.#designId = 'D' + Date.now().toString(36).toUpperCase();
    this.#renderTabs();
    this.#renderTray();
    this.#renderView();
    this.#bind();
    this.#restore(params.get('design'), true);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /* ---------- helpers ---------- */

  get #opts() { return { signal: this.#ac.signal }; }
  // The canvas is moved next to the gallery, outside this element, so look in both places.
  #q(sel) { return this.querySelector(sel) || this.#canvas?.querySelector(sel); }
  #qa(sel) {
    const own = [...this.querySelectorAll(sel)];
    const moved = this.#canvas && !this.contains(this.#canvas) ? [...this.#canvas.querySelectorAll(sel)] : [];
    return [...own, ...moved];
  }
  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.#cfg.currency || 'USD' }).format((cents || 0) / 100);
  }
  #viewCfg(id = this.#view) { return this.#cfg.views.find((v) => v.id === id); }
  #patchCfg(id) { return this.#cfg.patches.find((p) => p.id === id); }
  #find(uid) { return this.#patches.find((p) => p.uid === uid); }
  #charge() {
    const n = this.#patches.length;
    if (n === 0) return 0;
    return this.#cfg.addon?.[String(n)]?.price ?? null;
  }
  #inPoly(x, y, pts) {
    let inside = false;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const [xi, yi] = pts[i];
      const [xj, yj] = pts[j];
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }

  /* ---------- sizing ---------- */

  #clampSize(w) {
    const { min, max } = this.#cfg.size;
    return Math.round(Math.min(max, Math.max(min, w)) * 10) / 10;
  }
  #aspect(patchId) { return Number(this.#patchCfg(patchId)?.ratio) || 1.4; }
  // Patch width as % of the photo width.
  #wPct(inches) { return (inches / this.#cfg.size.photoWidth) * 100; }
  #heightIn(p) { return p.w / this.#aspect(p.patch); }
  #isResized(p) { return Math.abs(p.w - this.#cfg.size.default) > 0.05; }
  #sizeText(p) {
    return this.#cfg.strings.sizeValue
      .replace('{{ width }}', p.w.toFixed(1))
      .replace('{{ height }}', this.#heightIn(p).toFixed(1));
  }
  // Half extents of a patch in "width units" (% of photo width); artwork has a little transparent margin (~8%).
  #box(patchId, inches) {
    const a = (this.#wPct(inches) / 2) * 0.92;
    return [a, a / this.#aspect(patchId)];
  }
  // Axis-aligned half extents of a rotated patch, in width units.
  #extent(patchId, inches, deg) {
    const [a, b] = this.#box(patchId, inches);
    const t = (deg * Math.PI) / 180;
    const c = Math.abs(Math.cos(t));
    const s = Math.abs(Math.sin(t));
    return [a * c + b * s, a * s + b * c];
  }
  #normDeg(d) { let r = Math.round(d) % 360; if (r > 180) r -= 360; if (r <= -180) r += 360; return r; }

  /**
   * Returns the sleeve zone a patch fits in at (x, y) with width w (inches) and rotation r (degrees), or null
   * when any part of it would leave the sewing area or overlap another patch on the same view.
   * x is % of photo width, y % of photo height; the photo ratio converts between them.
   */
  #fit(patchId, x, y, w, viewId = this.#view, ignoreUid = null, r = 0) {
    const v = this.#viewCfg(viewId);
    const k = v.ratio || 1;
    const [a, b] = this.#box(patchId, w);
    const t = (r * Math.PI) / 180;
    const c = Math.cos(t);
    const s = Math.sin(t);
    const local = [[0, 0], [-a, -b], [a, -b], [-a, b], [a, b], [-a, 0], [a, 0], [0, -b], [0, b]];
    const pts = local.map(([lx, ly]) => [x + lx * c - ly * s, y + (lx * s + ly * c) * k]);
    const zone = v.zones.find((z) => pts.every(([px, py]) => this.#inPoly(px, py, z.points)));
    if (!zone) return null;
    const [ex, ey] = this.#extent(patchId, w, r);
    const clash = this.#patches.some((o) => {
      if (o.uid === ignoreUid || o.view !== viewId) return false;
      const [ox, oy] = this.#extent(o.patch, o.w, o.r || 0);
      return Math.abs(o.x - x) < ex + ox && Math.abs(o.y - y) / k < ey + oy;
    });
    return clash ? null : zone;
  }

  // First free spot in a zone, searching outward from its centre.
  #freeSpot(patchId, w, viewId, zone) {
    const xs = zone.points.map((p) => p[0]);
    const ys = zone.points.map((p) => p[1]);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const cands = [];
    for (let x = Math.min(...xs); x <= Math.max(...xs); x += 1) {
      for (let y = Math.min(...ys); y <= Math.max(...ys); y += 1.5) cands.push([x, y]);
    }
    cands.sort((a, b) => Math.hypot(a[0] - cx, a[1] - cy) - Math.hypot(b[0] - cx, b[1] - cy));
    return cands.find(([x, y]) => this.#fit(patchId, x, y, w, viewId)) || null;
  }

  #downPct(z, y) {
    const ys = z.points.map((p) => p[1]);
    const top = Math.min(...ys);
    const bottom = Math.max(...ys);
    return Math.round(((y - top) / (bottom - top)) * 100);
  }
  #pointFromEvent(e) {
    const r = this.#q('[data-stage]').getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100];
  }
  // Inline display so visibility never depends on a stylesheet rule for [hidden].
  #show(el, on) { if (!el) return; el.hidden = !on; el.style.display = on ? '' : 'none'; }
  #say(msg) { const s = this.#q('[data-status]'); if (s) s.textContent = msg; }

  /* ---------- rendering ---------- */

  #renderTabs() {
    const tabs = this.#q('[data-tabs]');
    tabs.replaceChildren(...this.#cfg.views.map((v) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.dataset.view = v.id;
      b.textContent = v.label;
      b.className = 'h-10 rounded-sm border px-4 text-xs uppercase tracking-[0.14em] cursor-pointer';
      return b;
    }));
  }

  #renderTray() {
    const tray = this.#q('[data-tray]');
    tray.replaceChildren(...this.#cfg.patches.map((p) => {
      const li = document.createElement('li');
      li.className = 'w-[4.5rem] shrink-0 md:w-auto';
      const b = document.createElement('button');
      b.type = 'button';
      b.dataset.patch = p.id;
      b.className = 'flex w-full flex-col items-center gap-1 rounded-sm border border-on-surface/15 bg-surface p-1.5 text-[11px] leading-tight text-on-surface hover:border-on-surface cursor-grab';
      // Sideways swipes scroll the tray on phones; tap adds, drag up drops onto the jacket.
      b.style.touchAction = 'pan-x';
      b.setAttribute('aria-label', p.name);
      const img = document.createElement('img');
      img.src = p.image;
      img.alt = '';
      img.draggable = false;
      img.className = 'aspect-[1.4/1] w-full object-contain pointer-events-none';
      const span = document.createElement('span');
      span.textContent = p.name;
      b.append(img, span);
      li.append(b);
      return li;
    }));
  }

  #renderView() {
    const v = this.#viewCfg();
    const img = this.#q('[data-stage-image]');
    img.src = v.image;
    // Warm the other view(s) so switching Front/Back is instant.
    this.#cfg.views.forEach((ov) => { if (ov.id !== v.id && !ov.warm) { ov.warm = true; const pre = new Image(); pre.decoding = 'async'; pre.src = ov.image; } });
    img.alt = v.label;
    // Keep the whole jacket on screen: cap the stage width by the viewport height.
    this.#q('[data-stage]').style.maxWidth = `calc((100vh - 260px) * ${v.ratio || 1})`;
    this.#q('[data-zones]').innerHTML = v.zones
      .map((z) => `<polygon points="${z.points.map((p) => p.join(',')).join(' ')}" fill="rgba(255,255,255,0.08)" stroke="currentColor" stroke-width="0.35" stroke-dasharray="1.2 1" vector-effect="non-scaling-stroke" class="text-on-surface/60"></polygon>`)
      .join('');
    this.#qa('[data-tabs] [role="tab"]').forEach((t) => {
      const on = t.dataset.view === this.#view;
      t.setAttribute('aria-selected', String(on));
      t.classList.toggle('bg-on-surface', on);
      t.classList.toggle('text-surface', on);
      t.classList.toggle('border-on-surface', true);
    });
    this.#renderPatches();
  }

  #renderPatches() {
    const layer = this.#q('[data-layer]');
    layer.replaceChildren(...this.#patches.filter((p) => p.view === this.#view).map((p) => {
      const cfg = this.#patchCfg(p.patch);
      const el = document.createElement('button');
      el.type = 'button';
      el.dataset.uid = p.uid;
      el.className = 'absolute -translate-x-1/2 -translate-y-1/2 cursor-move touch-none rounded-[2px] p-0 focus-visible:outline-2 focus-visible:outline-accent';
      this.#place(el, p);
      el.setAttribute('aria-label', `${cfg?.name || ''}, ${this.#zoneLabel(p)}, ${this.#sizeText(p)}. ${this.#cfg.strings.selected}`);
      const img = document.createElement('img');
      img.src = cfg?.image || '';
      img.alt = '';
      img.draggable = false;
      img.className = 'block w-full pointer-events-none drop-shadow-sm';
      el.append(img);
      if (this.#selected === p.uid) {
        el.style.outline = '2px solid currentColor';
        el.style.outlineOffset = '2px';
        ['nw', 'ne', 'sw', 'se'].forEach((c) => {
          const h = document.createElement('span');
          h.dataset.handle = c;
          h.setAttribute('aria-hidden', 'true');
          // 14px visual square with a ~40px invisible hit area for touch.
          h.className = 'absolute size-3.5 rounded-[2px] border border-surface bg-on-surface shadow before:absolute before:-inset-3 before:content-[""] '
            + { nw: '-left-2 -top-2 cursor-nwse-resize', ne: '-right-2 -top-2 cursor-nesw-resize', sw: '-left-2 -bottom-2 cursor-nesw-resize', se: '-right-2 -bottom-2 cursor-nwse-resize' }[c];
          el.append(h);
        });
        // Rotate handle: a round knob on a short stem above the patch.
        const stem = document.createElement('span');
        stem.setAttribute('aria-hidden', 'true');
        stem.className = 'pointer-events-none absolute left-1/2 -top-6 h-4 w-px -translate-x-1/2 bg-on-surface';
        const knob = document.createElement('span');
        knob.dataset.handle = 'rotate';
        knob.setAttribute('aria-hidden', 'true');
        knob.className = 'absolute left-1/2 -top-9 size-4 -translate-x-1/2 rounded-full border-2 border-on-surface bg-surface shadow cursor-grab before:absolute before:-inset-3 before:content-[""]';
        el.append(stem, knob);
        const tag = document.createElement('span');
        tag.dataset.tag = '';
        tag.setAttribute('aria-hidden', 'true');
        tag.className = 'pointer-events-none absolute left-1/2 top-full mt-3 -translate-x-1/2 whitespace-nowrap rounded-sm bg-on-surface px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-surface';
        el.append(tag);
        this.#place(el, p);
      }
      return el;
    }));
  }

  #place(el, p) {
    el.style.left = p.x + '%';
    el.style.top = p.y + '%';
    el.style.width = this.#wPct(p.w) + '%';
    el.style.rotate = (p.r || 0) + 'deg';
    const tag = el.querySelector('[data-tag]');
    if (tag) {
      tag.textContent = this.#sizeText(p) + (p.r ? ` · ${p.r}°` : '');
      tag.style.rotate = -(p.r || 0) + 'deg'; // keep the label upright
    }
  }

  #zoneLabel(p) {
    const v = this.#viewCfg(p.view);
    const z = v?.zones.find((zz) => zz.id === p.zone);
    return `${z?.label || ''} (${v?.label || ''})`;
  }

  #renderSizeControls() {
    const p = this.#find(this.#selected);
    const s = this.#cfg.strings;
    const range = this.#q('[data-size-range]');
    const on = !!p;
    this.#qa('[data-size-step], [data-size-range], [data-size-reset], [data-rot-step], [data-rot-range], [data-rot-reset]').forEach((c) => { c.disabled = !on; });
    this.#q('[data-size-readout]').textContent = on ? this.#sizeText(p) : '';
    this.#q('[data-rot-readout]').textContent = on ? `${p.r || 0}°` : '';
    this.#q('[data-size-hint]').textContent = on ? s.resizeHint : s.selectToResize;
    if (on) {
      range.value = String(p.w);
      this.#q('[data-rot-range]').value = String(p.r || 0);
      this.#q('[data-size-reset]').disabled = !this.#isResized(p);
      this.#q('[data-rot-reset]').disabled = !p.r;
    }
  }

  #update() {
    const s = this.#cfg.strings;
    const n = this.#patches.length;
    const charge = this.#charge();

    // Placed patches as compact chips: thumbnail (tap to select) + remove.
    const list = this.#q('[data-list]');
    this.#q('[data-count]').textContent = s.placedCount.replace('{{ count }}', n).replace('{{ max }}', this.#cfg.max);
    if (!n) {
      const li = document.createElement('li');
      li.className = 'text-xs text-on-surface-secondary';
      li.textContent = s.noPatches;
      list.replaceChildren(li);
    } else {
      list.replaceChildren(...this.#patches.map((p) => {
        const cfg = this.#patchCfg(p.patch);
        const li = document.createElement('li');
        li.className = 'relative';
        const pick = document.createElement('button');
        pick.type = 'button';
        pick.dataset.focus = p.uid;
        pick.title = `${cfg?.name} · ${this.#zoneLabel(p)} · ${this.#sizeText(p)}`;
        pick.setAttribute('aria-label', pick.title);
        pick.className = 'flex size-14 items-center justify-center rounded-sm border bg-surface p-1 cursor-pointer '
          + (this.#selected === p.uid ? 'border-on-surface' : 'border-on-surface/15 hover:border-on-surface/50');
        const img = document.createElement('img');
        img.src = cfg?.image || '';
        img.alt = '';
        img.className = 'max-h-full max-w-full object-contain';
        img.style.rotate = (p.r || 0) + 'deg';
        pick.append(img);
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.dataset.remove = p.uid;
        rm.setAttribute('aria-label', s.removePatch.replace('{{ name }}', cfg?.name || ''));
        rm.className = 'absolute -right-2 -top-2 inline-flex size-6 items-center justify-center rounded-full bg-on-surface text-xs leading-none text-surface hover:opacity-90 cursor-pointer before:absolute before:-inset-2 before:content-[""]';
        rm.textContent = '×';
        li.append(pick, rm);
        return li;
      }));
    }

    // Tray state
    this.#qa('[data-tray] [data-patch]').forEach((b) => {
      b.setAttribute('aria-disabled', String(n >= this.#cfg.max && !this.#selected));
    });

    // Summary
    const dl = this.#q('[data-summary]');
    const rows = [[s.jacket, this.#money(this.#cfg.basePrice)]];
    if (n) rows.push([s.charge.replace('{{ count }}', n), charge == null ? '—' : this.#money(charge)]);
    rows.push([s.total, charge == null ? '—' : this.#money(this.#cfg.basePrice + (charge || 0))]);
    dl.replaceChildren(...rows.flatMap(([k, val], i) => {
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = k;
      dd.textContent = val;
      dd.className = 'text-end';
      if (i === rows.length - 1) { dt.className = 'font-medium'; dd.className += ' font-medium'; }
      return [dt, dd];
    }));

    this.#say(this.#statusText());
    this.#show(this.#q('[data-panel]'), this.#isOpen || n > 0);
    this.#renderSizeControls();

    // Phone bar: live total.
    const barTotal = this.#q('[data-bar-total]');
    if (barTotal) {
      const total = charge == null ? '—' : this.#money(this.#cfg.basePrice + (charge || 0));
      barTotal.textContent = `${s.total} ${total}${n ? ` · ${s.count.replace('{{ count }}', n)}` : ''}`;
    }

    // Hide Buy it now while customizing (it would skip the patch charge).
    const pay = document.getElementById(this.#cfg.formId)?.querySelector('[data-payment-button]');
    if (pay) pay.hidden = n > 0;

    this.#writeProperties();
  }

  #sizesBlocked() {
    return !this.#cfg.size.verified && this.#patches.some((p) => this.#isResized(p));
  }

  #statusText() {
    const s = this.#cfg.strings;
    const n = this.#patches.length;
    if (!n) return '';
    if (!this.#cfg.addon) return s.unavailable;
    if (n < this.#cfg.min) return s.needMin;
    if (this.#charge() == null) return s.unavailable;
    if (this.#sizesBlocked()) return s.sizeBlocked;
    if (n >= this.#cfg.max) return s.atMax;
    return s.ready;
  }

  #isValid() {
    const n = this.#patches.length;
    if (n === 0) return true;
    return n >= this.#cfg.min && n <= this.#cfg.max && this.#charge() != null
      && this.#patches.every((p) => p.zone) && !this.#sizesBlocked();
  }

  #encode() {
    const data = this.#patches.map((p) => ({ p: p.patch, v: p.view, x: +p.x.toFixed(1), y: +p.y.toFixed(1), s: p.w, r: p.r || 0 }));
    return btoa(JSON.stringify(data)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  #designLink() {
    return `${this.#cfg.productUrl}?customizer=preview&design=${this.#encode()}`;
  }

  #writeProperties() {
    const s = this.#cfg.strings;
    const box = this.#q('[data-properties]');
    const host = this.closest('s-product-form') || document.getElementById(this.#cfg.formId)?.closest('s-product-form');
    const n = this.#patches.length;
    if (!n || !this.#isValid()) {
      box.replaceChildren();
      if (host) delete host.dataset.extraLines;
      return;
    }
    const charge = this.#charge();
    const link = this.#designLink();
    const props = [[s.propEdition, s.edition.replace('{{ count }}', n)]];
    this.#patches.forEach((p, i) => {
      const v = this.#viewCfg(p.view);
      const z = v.zones.find((zz) => zz.id === p.zone);
      // Unverified sizes never reach an order: they're blocked above, so these are standard-size patches.
      const size = this.#cfg.size.verified ? `${p.w.toFixed(2)} × ${this.#heightIn(p).toFixed(2)} in` : s.standardSize;
      const rot = p.r ? ` · ${s.rotated.replace('{{ degrees }}', p.r)}` : '';
      const detail = `${this.#patchCfg(p.patch)?.name} · ${z.label} (${v.label}) · ${s.down.replace('{{ percent }}', this.#downPct(z, p.y))} · x ${p.x.toFixed(1)}% / y ${p.y.toFixed(1)}% · ${size}${rot}`;
      props.push([s.propPatch.replace('{{ number }}', i + 1), detail]);
    });
    props.push([s.propCharge, this.#money(charge)]);
    props.push([s.propLink, link]);
    props.push(['_design_id', this.#designId]);

    box.replaceChildren(...props.map(([k, val]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${k}]`;
      input.value = val;
      input.setAttribute('form', this.#cfg.formId);
      return input;
    }));

    const addonVariant = this.#cfg.addon[String(n)];
    const extra = [{
      merchandiseId: String(addonVariant.id),
      quantity: 1,
      attributes: [
        { key: s.propFor, value: this.#cfg.productTitle },
        { key: s.propEdition, value: s.edition.replace('{{ count }}', n) },
        { key: s.propLink, value: link },
        { key: '_design_id', value: this.#designId },
      ],
    }];
    if (host) host.dataset.extraLines = JSON.stringify(extra);
  }

  /* ---------- actions ---------- */

  #add(patchId, x, y) {
    if (this.#patches.length >= this.#cfg.max) { this.#say(this.#cfg.strings.atMax); return; }
    const w = this.#cfg.size.default;
    let zone = x == null ? null : this.#fit(patchId, x, y, w);
    if (!zone) {
      // Tap-to-add (or a drop with no room): free spot on the sleeve with the fewest patches.
      const v = this.#viewCfg();
      const zones = [...v.zones].sort((a, b) =>
        this.#patches.filter((p) => p.view === v.id && p.zone === a.id).length
        - this.#patches.filter((p) => p.view === v.id && p.zone === b.id).length);
      for (const z of zones) {
        const spot = this.#freeSpot(patchId, w, v.id, z);
        if (spot) { [x, y] = spot; zone = z; break; }
      }
      if (!zone) { this.#say(this.#cfg.strings.noFit); return; }
    }
    const uid = 'p' + Math.random().toString(36).slice(2, 8);
    this.#patches.push({ uid, patch: patchId, view: this.#view, zone: zone.id, x, y, w, r: 0 });
    this.#selected = uid;
    this.#renderPatches();
    this.#update();
  }

  #remove(uid) {
    this.#patches = this.#patches.filter((p) => p.uid !== uid);
    if (this.#selected === uid) this.#selected = null;
    this.#renderPatches();
    this.#update();
  }

  #moveTo(p, x, y) {
    const z = this.#fit(p.patch, x, y, p.w, p.view, p.uid, p.r || 0);
    if (!z) return false;
    p.x = x; p.y = y; p.zone = z.id;
    return true;
  }

  // Resize keeping the centre; returns false (and leaves the patch as it was) when it doesn't fit.
  #resize(p, inches) {
    const w = this.#clampSize(inches);
    if (w === p.w) return true;
    const z = this.#fit(p.patch, p.x, p.y, w, p.view, p.uid, p.r || 0);
    if (!z) return false;
    p.w = w; p.zone = z.id;
    return true;
  }

  #applySize(inches) {
    const p = this.#find(this.#selected);
    if (!p) return;
    if (!this.#resize(p, inches)) this.#say(this.#cfg.strings.noFit);
    else this.#update();
    const el = this.#q(`[data-layer] [data-uid="${p.uid}"]`);
    if (el) this.#place(el, p);
    this.#renderSizeControls();
  }

  // Rotate around the centre; returns false (no change) when the turned patch wouldn't fit.
  #rotate(p, deg) {
    let r = this.#normDeg(deg);
    // Gentle snap to straight / quarter turns.
    for (const snap of [0, 90, -90, 180]) if (Math.abs(r - snap) <= 3) r = snap === 180 ? 180 : snap;
    if (r === (p.r || 0)) return true;
    const z = this.#fit(p.patch, p.x, p.y, p.w, p.view, p.uid, r);
    if (!z) return false;
    p.r = r; p.zone = z.id;
    return true;
  }

  #applyRotation(deg) {
    const p = this.#find(this.#selected);
    if (!p) return;
    if (!this.#rotate(p, deg)) this.#say(this.#cfg.strings.noFit);
    else this.#update();
    const el = this.#q(`[data-layer] [data-uid="${p.uid}"]`);
    if (el) this.#place(el, p);
    this.#renderSizeControls();
  }

  #restore(encoded, fromLink) {
    if (!encoded) return false;
    try {
      const json = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
      const data = JSON.parse(json);
      this.#patches = [];
      data.slice(0, this.#cfg.max).forEach((d) => {
        if (!this.#patchCfg(d.p) || !this.#viewCfg(d.v)) return;
        const w = this.#clampSize(Number(d.s) || this.#cfg.size.default);
        const r = this.#normDeg(Number(d.r) || 0);
        const z = this.#fit(d.p, d.x, d.y, w, d.v, null, r);
        if (!z) return;
        this.#patches.push({ uid: 'p' + Math.random().toString(36).slice(2, 8), patch: d.p, view: d.v, zone: z.id, x: d.x, y: d.y, w, r });
      });
      if (fromLink) this.#open(true);
      this.#renderPatches();
      return true;
    } catch (e) { return false; /* ignore malformed links */ }
  }

  get #storeKey() { return `pc-design:${this.#cfg.productUrl}`; }

  #save() {
    try { localStorage.setItem(this.#storeKey, this.#encode()); } catch (e) { /* storage unavailable */ }
    this.#q('[data-saved]').textContent = this.#cfg.strings.saved;
  }

  // Canvas mode: the canvas takes the gallery's place in the main image area.
  #open(force) {
    const show = force ?? !this.#isOpen;
    const canvas = this.#canvas;
    const gallery = this.#gallery;
    if (show && gallery && canvas && this.contains(canvas)) {
      canvas.style.setProperty('--gallery-ratio', gallery.style.getPropertyValue('--gallery-ratio') || '0.8');
      gallery.after(canvas);
    }
    // First open on this device: bring back a saved design.
    if (show && !this.#patches.length) {
      let saved = null;
      try { saved = localStorage.getItem(this.#storeKey); } catch (e) { /* storage unavailable */ }
      if (saved) this.#restore(saved, false);
    }
    this.#isOpen = show;
    // Match the gallery's footprint (photo + thumbnails) on tablet/desktop; full width on phones.
    if (show && gallery && canvas) {
      const gw = gallery.getBoundingClientRect().width;
      canvas.style.width = window.matchMedia('(min-width: 768px)').matches && gw ? gw + 'px' : '';
    }
    if (gallery) this.#show(gallery, !show);
    this.#show(canvas, show);
    this.#focusMode(show);
    const btn = this.#q('[data-open]');
    btn.setAttribute('aria-expanded', String(show));
    this.#q('[data-open-label]').hidden = show;
    this.#q('[data-close-label]').hidden = !show;
    this.#update();
    if (show) {
      const r = canvas.getBoundingClientRect();
      if (r.top < 0 || r.top > window.innerHeight * 0.6) canvas.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } else if (gallery) {
      const r = gallery.getBoundingClientRect();
      if (r.bottom < 0) gallery.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  /**
   * Focus mode while customizing: keep title, price, design summary, size and Add to cart together on one
   * screen by tucking away the quantity picker (custom jackets are one at a time) and the info accordions,
   * and show the phone bar (size, live total, Add to cart). Everything is restored on BACK TO PHOTOS.
   */
  #observer = null;
  #form() { return document.getElementById(this.#cfg.formId); }
  #sizeRadios() {
    return [...(this.#form()?.querySelectorAll('input[type="radio"]') || [])].filter((r) => /size/i.test(r.name));
  }
  #tuckQuantity(on) {
    const form = this.#form();
    const wrap = form?.querySelector('label[for="product-quantity"]')?.parentElement;
    if (wrap) wrap.style.display = on ? 'none' : '';
    const qty = form?.querySelector('[name="quantity"]');
    if (on && qty) qty.value = '1';
  }
  #syncBar() {
    const select = this.#q('[data-size-select]');
    const radios = this.#sizeRadios();
    if (select) {
      select.hidden = !radios.length;
      select.replaceChildren(...radios.map((r) => {
        const opt = document.createElement('option');
        opt.value = r.value;
        opt.textContent = r.value;
        opt.disabled = r.disabled;
        opt.selected = r.checked;
        return opt;
      }));
    }
    const atc = this.#q('[data-atc]');
    if (atc) atc.disabled = !this.#form()?.querySelector('[type="submit"][name="add"]');
  }
  #focusMode(on) {
    const host = this.closest('s-product-form');
    const col = host?.parentElement;
    if (col) {
      [...col.children].forEach((ch) => {
        if (ch === host || !(host.compareDocumentPosition(ch) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
        if (on && ch.style.display !== 'none') { ch.dataset.pcTucked = ''; ch.style.display = 'none'; }
        if (!on && 'pcTucked' in ch.dataset) { ch.style.display = ''; delete ch.dataset.pcTucked; }
      });
    }
    this.#tuckQuantity(on);
    const bar = this.#q('[data-mobile-bar]');
    this.#show(bar, on);
    document.body.style.paddingBottom = on && window.matchMedia('(max-width: 767px)').matches ? '72px' : '';
    this.#observer?.disconnect();
    this.#observer = null;
    if (on) {
      this.#syncBar();
      // Size changes re-render the product form; re-apply and resync after each render.
      let raf = 0;
      this.#observer = new MutationObserver((list) => {
        // Ignore the customizer's own redraws (dragging, chips); only product-form re-renders need a resync.
        if (list.every((m) => this.contains(m.target))) return;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => { this.#tuckQuantity(true); this.#syncBar(); });
      });
      if (host) this.#observer.observe(host, { childList: true, subtree: true });
    }
  }

  /* ---------- events ---------- */

  #bind() {
    const o = this.#opts;
    this.#q('[data-open]').addEventListener('click', () => this.#open(), o);
    this.#q('[data-close]').addEventListener('click', () => { this.#open(false); this.#q('[data-open]').focus(); }, o);
    this.#q('[data-save]').addEventListener('click', () => this.#save(), o);
    this.#q('[data-reset]').addEventListener('click', () => {
      this.#patches = [];
      this.#selected = null;
      this.#designId = 'D' + Date.now().toString(36).toUpperCase();
      try { localStorage.removeItem(this.#storeKey); } catch (e) { /* storage unavailable */ }
      this.#q('[data-saved]').textContent = '';
      this.#renderPatches();
      this.#update();
    }, o);

    // Size controls
    this.#q('[data-size-range]').addEventListener('input', (e) => this.#applySize(Number(e.target.value)), o);
    this.#qa('[data-size-step]').forEach((b) => b.addEventListener('click', () => {
      const p = this.#find(this.#selected);
      if (p) this.#applySize(p.w + Number(b.dataset.sizeStep) * 0.25);
    }, o));
    this.#q('[data-size-reset]').addEventListener('click', () => this.#applySize(this.#cfg.size.default), o);

    // Rotation controls
    this.#q('[data-rot-range]').addEventListener('input', (e) => this.#applyRotation(Number(e.target.value)), o);
    this.#qa('[data-rot-step]').forEach((b) => b.addEventListener('click', () => {
      const p = this.#find(this.#selected);
      if (p) this.#applyRotation((p.r || 0) + Number(b.dataset.rotStep) * 15);
    }, o));
    this.#q('[data-rot-reset]').addEventListener('click', () => this.#applyRotation(0), o);

    // Phone bar: size and Add to cart drive the real product form.
    this.#q('[data-size-select]')?.addEventListener('change', (e) => {
      const radio = this.#sizeRadios().find((r) => r.value === e.target.value);
      radio?.click();
    }, o);
    this.#q('[data-atc]')?.addEventListener('click', () => {
      this.#form()?.querySelector('[type="submit"][name="add"]')?.click();
    }, o);

    this.#q('[data-tabs]').addEventListener('click', (e) => {
      const t = e.target.closest('[data-view]');
      if (!t) return;
      this.#view = t.dataset.view;
      this.#selected = null;
      this.#renderView();
      this.#update();
    }, o);

    this.#q('[data-list]').addEventListener('click', (e) => {
      const rm = e.target.closest('[data-remove]');
      if (rm) { this.#remove(rm.dataset.remove); return; }
      const f = e.target.closest('[data-focus]');
      if (f) {
        const p = this.#find(f.dataset.focus);
        if (!p) return;
        if (!this.#isOpen) this.#open(true);
        this.#view = p.view;
        this.#selected = p.uid;
        this.#renderView();
        this.#update();
        this.#q(`[data-layer] [data-uid="${p.uid}"]`)?.focus();
      }
    }, o);

    // Tray: tap adds (or replaces the selected patch's design); drag drops onto the jacket.
    const tray = this.#q('[data-tray]');
    tray.addEventListener('pointerdown', (e) => {
      const b = e.target.closest('[data-patch]');
      if (!b) return;
      this.#drag = { type: 'tray', patch: b.dataset.patch, startX: e.clientX, startY: e.clientY, moved: false, ghost: null };
      b.setPointerCapture?.(e.pointerId);
    }, o);
    tray.addEventListener('pointermove', (e) => {
      const d = this.#drag;
      if (!d || d.type !== 'tray') return;
      if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 8) return;
      d.moved = true;
      if (!d.ghost) {
        d.ghost = document.createElement('img');
        d.ghost.src = this.#patchCfg(d.patch).image;
        d.ghost.alt = '';
        d.ghost.className = 'pointer-events-none fixed z-50 w-20 -translate-x-1/2 -translate-y-1/2 opacity-80';
        document.body.append(d.ghost);
      }
      d.ghost.style.left = e.clientX + 'px';
      d.ghost.style.top = e.clientY + 'px';
    }, o);
    const endTray = (e) => {
      const d = this.#drag;
      if (!d || d.type !== 'tray') return;
      this.#drag = null;
      d.ghost?.remove();
      if (e.type === 'pointercancel') return;
      if (!d.moved) {
        const sel = this.#find(this.#selected);
        if (sel) {
          // Swapping artwork: keep size if the new design fits, else fall back to the standard size.
          const prev = sel.patch;
          sel.patch = d.patch;
          if (!this.#fit(sel.patch, sel.x, sel.y, sel.w, sel.view, sel.uid, sel.r || 0)) {
            if (this.#fit(sel.patch, sel.x, sel.y, this.#cfg.size.default, sel.view, sel.uid, sel.r || 0)) sel.w = this.#cfg.size.default;
            else { sel.patch = prev; this.#say(this.#cfg.strings.noFit); return; }
          }
          this.#renderPatches();
          this.#update();
        } else this.#add(d.patch);
        return;
      }
      const r = this.#q('[data-stage]').getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
      const [x, y] = this.#pointFromEvent(e);
      if (this.#fit(d.patch, x, y, this.#cfg.size.default)) this.#add(d.patch, x, y);
      else this.#say(this.#cfg.strings.placedOn);
    };
    tray.addEventListener('pointerup', endTray, o);
    tray.addEventListener('pointercancel', endTray, o);

    // Stage: drag placed patches, drag corner handles to resize, tap empty space to deselect.
    const layer = this.#q('[data-layer]');
    layer.addEventListener('pointerdown', (e) => {
      const el = e.target.closest('[data-uid]');
      if (!el) { this.#selected = null; this.#renderPatches(); this.#update(); return; }
      e.preventDefault();
      const p = this.#find(el.dataset.uid);
      const handle = e.target.closest('[data-handle]');
      if (handle) {
        this.#drag = { type: handle.dataset.handle === 'rotate' ? 'rotate' : 'resize', p, el };
        layer.setPointerCapture?.(e.pointerId);
        return;
      }
      this.#selected = p.uid;
      const [px, py] = this.#pointFromEvent(e);
      // Keep the element under the finger as-is (re-rendering it mid-touch drops the drag on iPhone);
      // handles appear on release.
      this.#drag = { type: 'patch', p, el, dx: p.x - px, dy: p.y - py, fx: p.x, fy: p.y };
      el.style.zIndex = '10';
      layer.setPointerCapture?.(e.pointerId);
    }, o);
    // One update per frame keeps dragging smooth on phones.
    let moveRaf = 0;
    let lastMove = null;
    layer.addEventListener('pointermove', (e) => {
      if (!this.#drag) return;
      e.preventDefault();
      lastMove = { clientX: e.clientX, clientY: e.clientY };
      if (moveRaf) return;
      moveRaf = requestAnimationFrame(() => {
        moveRaf = 0;
        if (lastMove) this.#dragMove(lastMove);
      });
    }, o);
    this.#dragMove = (e) => {
      const d = this.#drag;
      if (!d) return;
      const [px, py] = this.#pointFromEvent(e);
      if (d.type === 'patch') {
        // The patch follows the finger anywhere, so it can cross the body to the other sleeve.
        // Where it fits it lands there; along an edge it slides; elsewhere it shows faded until it fits again.
        const x = px + d.dx;
        const y = py + d.dy;
        d.fx = x; d.fy = y;
        if (this.#moveTo(d.p, x, y)) {
          this.#place(d.el, d.p);
          d.el.style.opacity = '';
        } else {
          // Slide along the edge so a release here lands as close as possible.
          if (!this.#moveTo(d.p, x, d.p.y)) this.#moveTo(d.p, d.p.x, y);
          d.el.style.left = x + '%';
          d.el.style.top = y + '%';
          d.el.style.opacity = '0.55';
        }
      } else if (d.type === 'resize') {
        // Proportional: the corner farther from the centre (in artwork terms) sets the width.
        const v = this.#viewCfg(d.p.view);
        const halfWpct = Math.max(Math.abs(px - d.p.x), (Math.abs(py - d.p.y) * this.#aspect(d.p.patch)) / (v.ratio || 1));
        const inches = ((halfWpct * 2) / 100) * this.#cfg.size.photoWidth;
        if (this.#resize(d.p, inches)) {
          this.#place(d.el, d.p);
          this.#renderSizeControls();
        }
      } else if (d.type === 'rotate') {
        // Angle from the patch centre to the finger/cursor; 0° = knob straight up.
        const box = d.el.getBoundingClientRect();
        const cx = box.left + box.width / 2;
        const cy = box.top + box.height / 2;
        const deg = Math.round((Math.atan2(e.clientX - cx, cy - e.clientY) * 180) / Math.PI);
        if (this.#rotate(d.p, deg)) {
          this.#place(d.el, d.p);
          this.#renderSizeControls();
        }
      }
    };
    const endPatch = () => {
      const d = this.#drag;
      if (!['patch', 'resize', 'rotate'].includes(d?.type)) return;
      if (moveRaf) { cancelAnimationFrame(moveRaf); moveRaf = 0; if (lastMove) this.#dragMove(lastMove); }
      lastMove = null;
      if (d.type === 'patch' && !this.#fit(d.p.patch, d.fx, d.fy, d.p.w, d.p.view, d.p.uid, d.p.r || 0)) {
        // Released where it can't sit (off a sleeve or on another patch): it settles at the nearest spot it fits.
        this.#say(this.#cfg.strings.noFit);
      }
      const uid = d.p.uid;
      this.#drag = null;
      this.#renderPatches();
      this.#update();
      this.#q(`[data-layer] [data-uid="${uid}"]`)?.focus({ preventScroll: true });
    };
    layer.addEventListener('pointerup', endPatch, o);
    layer.addEventListener('pointercancel', endPatch, o);

    // Keyboard: arrows nudge, + / - resize, Delete removes.
    layer.addEventListener('keydown', (e) => {
      const el = e.target.closest('[data-uid]');
      if (!el) return;
      const p = this.#find(el.dataset.uid);
      this.#selected = p.uid;
      const step = e.shiftKey ? 3 : 1;
      const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (moves[e.key]) {
        e.preventDefault();
        if (this.#moveTo(p, p.x + moves[e.key][0], p.y + moves[e.key][1])) {
          this.#place(el, p);
          this.#update();
        }
      } else if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '_') {
        e.preventDefault();
        this.#applySize(p.w + (e.key === '+' || e.key === '=' ? 0.25 : -0.25));
      } else if (e.key === '[' || e.key === ']') {
        e.preventDefault();
        this.#applyRotation((p.r || 0) + (e.key === ']' ? 15 : -15));
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        this.#remove(p.uid);
      }
    }, o);

    // Block add to cart while the design is incomplete (runs before <s-product-form>'s handler).
    const host = this.closest('s-product-form') || document.getElementById(this.#cfg.formId)?.closest('s-product-form') || document;
    host.addEventListener('submit', (e) => {
      if (e.target?.id !== this.#cfg.formId) return;
      this.#writeProperties();
      if (this.#isValid()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.#say(this.#statusText());
      this.#show(this.#q('[data-panel]'), true);
      this.#q('[data-status]').scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, { capture: true, signal: this.#ac.signal });
  }
}

if (!customElements.get('s-patch-customizer')) customElements.define('s-patch-customizer', SPatchCustomizer);
